// Where the back arrow on a detail page should go.
//
// THE RULE: back returns the operator to what they were LOOKING AT, not to where the object
// sits in the hierarchy. Those are the same thing only when you arrived from the object's
// own list.
//
// What was wrong (found by walking the UI, 2026-08-13): every detail page hardcoded its
// parent, so the back arrow and the browser's back button disagreed on all four.
//
//   /lines/120        → /subscribers/39   — the right customer, but the OVERVIEW tab, not the
//                                           Lines tab the operator was working through
//   /devices/:mac     → /devices          — the global device list, leaving the customer
//   /locations/138    → /locations        — the global address list, ditto
//   /dids/:e164       → /dids             — the global number list, ditto
//
// The line page is the one that bites: tear a line down, press back, and land on Overview
// rather than the list of lines you were part-way through.
//
// HOW IT WORKS. Every link INTO a detail page carries `state: backTo(label, href)` naming
// the screen it left. This hook reads it and falls back to the page's hierarchical parent
// when there is none — a bookmark, a pasted URL, a fresh tab — so the arrow is never dead.
// React Router keeps the state on its history entry, so it survives back and forward, and
// `useLocation().state` is null after a hard refresh, which is exactly when the fallback is
// the right answer anyway.

import { useLocation } from 'react-router-dom';

/**
 * Build the `state` for a Link into a detail page.
 *
 * The label matters as much as the href: "← Lyons Fire Protection District" and
 * "← Devices" are different promises, and an arrow that says one and does the other is
 * the same fault as a disabled button with no reason given.
 *
 * @param label  what the arrow should say, e.g. 'Lyons Fire Protection District'
 * @param href   where it should go, INCLUDING any tab hash, e.g. '/subscribers/39#lines'
 */
export function backTo(label, href) {
  return { backTo: { label, href } };
}

/**
 * @param fallbackLabel  the arrow's text when nothing was passed in
 * @param fallbackHref   the hierarchical parent, e.g. '/devices'
 * @returns {{label, href}}
 */
export default function useBackTo(fallbackLabel, fallbackHref) {
  const { state } = useLocation();
  const from = state?.backTo;
  // Only trust a state object that has both halves. A half-filled one would render an arrow
  // with no text or a link to nowhere, and silently falling back is better than either.
  if (from?.label && from?.href) return from;
  return { label: fallbackLabel, href: fallbackHref };
}
